if (!customElements.get('sticky-atc-bar-minimalist')) {
  customElements.define(
    'sticky-atc-bar-minimalist',
    class StickyAtcBarMinimalist extends HTMLElement {
      constructor() {
        super();

        this.classes = {
          isOpen: 'sticky-atc-bar--show',
        };

        this.submitEl.addEventListener('click', this.onClick.bind(this));
      }

      get submitEl() {
        return (this._submitEl = this._submitEl || this.querySelector('[type="submit"]'));
      }

      get mainSectionId() {
        return this.mainProductInfo ? this.mainProductInfo.dataset.section : this.dataset.section;
      }

      // The price block rendered next to the main add-to-cart button.
      get mainPriceEl() {
        return document.getElementById(`price-${this.mainSectionId}`);
      }

      get mainSubmitEl() {
        return document.getElementById(`ProductSubmitButton-${this.mainSectionId}`);
      }

      connectedCallback() {
        this.productFormActions = document.querySelector('.main-product-form');
        this.mainProductInfo = document.querySelector('product-info');

        this.init();

        this.cartErrorUnsubscriber = FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartError, () => {
          this.resetButtonLoading();
          this.scrollToTop(this.productFormActions);
        });

        this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, () => {
          this.resetButtonLoading();
        });
      }

      disconnectedCallback() {
        if (this.observer) {
          this.observer.disconnect();
        }

        if (this.mainObserver) {
          this.mainObserver.disconnect();
        }

        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }

        if (this.cartErrorUnsubscriber) {
          this.cartErrorUnsubscriber();
        }

        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
      }

      init() {
        if (!this.productFormActions) {
          this.classList.remove(this.classes.isOpen);
          return;
        }

        // Show the bar only while the main add-to-cart button is off screen
        // (original theme behaviour, reinstated 26 Aug 2026).
        const rootMargin = `${this.productFormActions.offsetHeight}px 0px 0px 0px`;
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              this.classList.toggle(this.classes.isOpen, !entry.isIntersecting);
            });
          },
          { threshold: 1, rootMargin }
        );
        this.observer.observe(this.productFormActions);

        this.syncWithMainProductForm();
      }

      /**
       * Keep the bar in step with the main product form.
       *
       * The bar mirrors the main product's price node rather than formatting a price
       * from a variant object. The theme changes variants down two different paths —
       * handleUpdateProductInfo (same product, publishes variantChange) and
       * handleSwapProduct (linked product URLs, publishes nothing) — so watching the
       * DOM is the only way to stay correct on both. It also means the bar can never
       * disagree with the price shown beside the main add-to-cart button.
       */
      syncWithMainProductForm() {
        this.syncFromMainProduct();

        const scope =
          document.getElementById(`shopify-section-${this.mainSectionId}`) || this.mainProductInfo || document.body;

        this.mainObserver = new MutationObserver(() => {
          if (this._syncQueued) return;
          this._syncQueued = true;
          window.requestAnimationFrame(() => {
            this._syncQueued = false;
            this.syncFromMainProduct();
          });
        });

        this.mainObserver.observe(scope, {
          childList: true,
          subtree: true,
          characterData: true,
          attributes: true,
          attributeFilter: ['class', 'disabled'],
        });

        // Fast path for the same-product variant change.
        this.variantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
          (event) => {
            if (!event || !event.data) return;
            if (event.data.sectionId && event.data.sectionId !== this.mainSectionId) return;
            this.syncFromMainProduct();
          }
        );
      }

      syncFromMainProduct() {
        this.syncPrice();
        this.syncButton();
      }

      syncPrice() {
        const source = this.mainPriceEl && this.mainPriceEl.querySelector('.f-price');
        const target = this.querySelector('.f-price');
        if (!source || !target) return;

        const clone = source.cloneNode(true);

        // Drop main-product-only chrome that shouldn't ride along into the bar.
        clone
          .querySelectorAll('.mf-info-icon__link, .whats-this-link, .toggle-grade-drawer, .infoBth')
          .forEach((node) => node.remove());
        clone.classList.remove('f-price--large', 'mf-price');

        // Two-line layout: the compare-at is nested inside the main price span
        // and the Save badge sits outside it, so no CSS can seat them together
        // under the main price. Restructure the clone instead: move both into
        // a subline that renders beneath the main price.
        const strike = clone.querySelector('.f-price-item--sale .f-price-item--regular');
        if (strike) {
          const subline = document.createElement('span');
          subline.className = 'mf-bar-subline';
          subline.appendChild(strike);
          const badges = clone.querySelector('.mf-product-badges');
          if (badges) subline.appendChild(badges);
          clone.appendChild(subline);
        }

        // Idempotent: no write when nothing changed, so our own mutations settle.
        if (clone.className === target.className && clone.innerHTML === target.innerHTML) return;

        target.className = clone.className;
        target.innerHTML = clone.innerHTML;
      }

      syncButton() {
        const mainButton = this.mainSubmitEl;
        const barButton = this.querySelector('[name="add"]');
        if (!mainButton || !barButton) return;

        const disabled = mainButton.hasAttribute('disabled');
        if (disabled) {
          barButton.setAttribute('disabled', 'disabled');
        } else {
          barButton.removeAttribute('disabled');
        }

        const mainText = mainButton.querySelector('.btn__text');
        const barText = barButton.querySelector('.btn__text');
        if (mainText && barText && barText.textContent.trim() !== mainText.textContent.trim()) {
          barText.textContent = mainText.textContent.trim();
        }
      }

      onClick() {
        this.submitEl.setAttribute('aria-disabled', 'true');
        this.submitEl.classList.add('btn--loading');
      }

      resetButtonLoading() {
        this.submitEl.removeAttribute('aria-disabled');
        this.submitEl.classList.remove('btn--loading');
      }

      scrollToTop(target, offset = 200) {
        if (!target) return;

        const scrollIntoView = (selector, offset) => {
          window.scrollTo({
            behavior: 'smooth',
            top: selector.getBoundingClientRect().top - document.body.getBoundingClientRect().top - offset,
          });
        };

        scrollIntoView(target, offset);
      }
    }
  );
}
